import { useDispatch, useSelector } from "react-redux"
import { Navigate, useNavigate } from "react-router"

import { Card } from "components/Card/Card"
import { FormPage } from "components/Form/Form"
import profileActions from "containers/Profile/services/actions"
import surveyActions from "containers/Survey/services/actions"
import {
  getCurrentPatient,
  getCurrentSelectedPatientId,
} from "containers/Survey/services/selector"
import { SURVEY_PAGE } from "core/constants"
import { languages } from "lib/languages"
import { capitalize, getMissingBirthTraits } from "lib/utils"
import { FOR_PATIENT } from "models/survey/constants"
import { Gender, Relative } from "types/entity"
import { InputFormProps, InputNames } from "types/props"

/*
 * Asked right after the patient choice when the selected patient (main
 * account or relative) lacks identity traits. Birth traits can be skipped,
 * the relative gender cannot.
 */
const CompletePatientInfo = (): JSX.Element => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const selectedPatientId = useSelector(getCurrentSelectedPatientId)
  const patient = useSelector(getCurrentPatient)

  const isRelative = selectedPatientId !== FOR_PATIENT
  const firstname = capitalize(patient?.firstname || "")
  const traitLabel = (yours: string, relatives: string) =>
    isRelative ? relatives.replace("$RELATIVE_NAME$", firstname) : yours
  const missingTraits = getMissingBirthTraits(patient)
  const missingGender =
    isRelative &&
    ![Gender.MALE, Gender.FEMALE].includes(patient?.gender as Gender)

  if (!patient || (!missingGender && !missingTraits.length)) {
    return <Navigate to={SURVEY_PAGE} replace />
  }

  const inputs: InputFormProps[] = []
  if (missingGender) {
    inputs.push({
      name: InputNames.GENDER,
      required: true,
      keepLabelFormat: true,
      label: `${capitalize(languages.inputThe("gender"))}${firstname ? ` de ${firstname}` : ""}`,
    })
  }
  if (missingTraits.includes(InputNames.FIRST_BIRTH_FIRSTNAME)) {
    inputs.push({
      name: InputNames.FIRST_BIRTH_FIRSTNAME,
      required: false,
      keepLabelFormat: true,
      hidePseudoName: true,
      label: traitLabel(
        languages.enterYourBirthFirstname,
        languages.enterRelativeBirthFirstname,
      ),
    })
  }
  if (missingTraits.includes(InputNames.BIRTH_LASTNAME)) {
    inputs.push({
      name: InputNames.BIRTH_LASTNAME,
      required: false,
      keepLabelFormat: true,
      hidePseudoName: true,
      label: traitLabel(
        languages.enterYourBirthLastname,
        languages.enterRelativeBirthLastname,
      ),
    })
  }
  if (missingTraits.includes(InputNames.BIRTH_LOCATION)) {
    // FormPage skips a single hidden input at a time: keep the insee code
    // right after the visible location input, and nothing after it
    inputs.push(
      {
        name: InputNames.BIRTH_LOCATION,
        required: false,
        keepLabelFormat: true,
        label: traitLabel(
          languages.enterYourBirthLocation,
          languages.enterRelativeBirthLocation,
        ),
      },
      { name: InputNames.INSEE_CODE, label: "", type: "hidden" },
    )
  }

  // Only the inputs listed in `accepted` are saved: on skip, the value still
  // being typed is dropped
  const save = (values: any, accepted: string[]) => {
    const payload: Partial<Relative> = {}
    if (accepted.includes(InputNames.GENDER)) payload.gender = values.gender
    if (accepted.includes(InputNames.FIRST_BIRTH_FIRSTNAME) && values.first_birth_firstname)
      payload.first_birth_firstname = values.first_birth_firstname
    if (accepted.includes(InputNames.BIRTH_LASTNAME) && values.birth_lastname)
      payload.birth_lastname = values.birth_lastname
    if (accepted.includes(InputNames.BIRTH_LOCATION) && values.insee_code)
      payload.birth_location = values.insee_code
    if (!Object.keys(payload).length) return
    dispatch(
      isRelative
        ? profileActions.changeRelativeRequest({
            id: selectedPatientId as number,
            ...payload,
          })
        : profileActions.changePatientRequest(payload),
    )
  }

  return (
    <Card>
      <FormPage
        loading={false}
        inputs={inputs}
        // the birthdate is read by the city search, in the date input format
        prospect={{
          ...patient,
          birthdate: patient.birthdate?.format?.("DDMMYYYY"),
        } as any}
        onFinish={(values) => {
          save(values, inputs.map((input) => input.name))
          navigate(SURVEY_PAGE)
        }}
        acceptText={languages.next}
        skipText={languages.later}
        onSkip={(values, accepted) => {
          save(values, accepted)
          navigate(SURVEY_PAGE)
        }}
        onCancel={() => {
          dispatch(surveyActions.previous())
          navigate(SURVEY_PAGE)
        }}
      />
    </Card>
  )
}

export default CompletePatientInfo
